// src/components/instructor/OpsSchedulePanel.js
//
// Scheduling pilot (Pass 42) — the operations schedule interface.
// Real-time (onSnapshot) view of the canonical `bookings` for one org, with
// Today / Tomorrow / date / week views, instructor + unassigned + cancelled
// filters, conflict indicators, and full ops actions (create / edit / assign /
// reassign / unassign / cancel / restore) — all routed through the canonical
// booking writers so every change audits + notifies + updates live.

import React, { useState, useMemo, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  createManualBooking, updateBooking, assignInstructorToBooking,
  unassignBooking, cancelBooking, restoreBooking, getBookingInternal,
} from '../../utils/booking';
import { useOrgSchedule, orgDateKey } from '../../hooks/useOrgSchedule';
import SeasonOverview from './SeasonOverview';
import {
  PAYMENT_STATES, SCHEDULE_ENTRY_TYPES, CANCELLATION_RESOLUTIONS, DISCIPLINES, SKILL_LABELS,
  isClientBooking, checkDisciplineCompatibility, deriveDurationCategory, formatAdditionalGuests, parseAdditionalGuests,
  assessOperationalReadiness, deriveSeasonId, isHistoricalSeason, seasonLabel, SEASONS,
} from '../../utils/scheduleModel';
import { resolveInstructorCategory } from '../../utils/instructorCategory';
import { wallClockToInstant, zonedDateKey, zonedTimeLabel } from '../../utils/opsTime';
import { resolveOrgSeasonId, resolveOrgTimezone } from '../../utils/organizationRuntime';
import { getMembersForSeason, splitTeachingStaff } from '../../utils/seasonScope';
import { isGridHold, isCoveredGridHold } from '../../utils/operationsWorkspace';

// Pass 59B — user-facing hold label (never expose internal source/slot codes).
const HOLD_SLOT_LABEL = { FULL_DAY: 'Full day', AM: 'AM', PM: 'PM', NIGHT: 'Night' };
const holdSlotLabel = (b = {}) => HOLD_SLOT_LABEL[b.operationalSlot] || 'Hold';

// Pass 82C.1 — the generic "current season" is now org-derived (resolveOrgSeasonId), not a module-global constant.

const C = {
  card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.08)',
  blue: '#2563EB', blueDim: 'rgba(37,99,235,0.08)', green: '#16A34A', greenDim: 'rgba(22,163,74,0.09)',
  amber: '#D97706', amberDim: 'rgba(217,119,6,0.10)', red: '#DC2626', redDim: 'rgba(220,38,38,0.08)',
  t1: '#1C1B18', t2: '#57564F', t3: '#8A8980',
};
const font = "'DM Sans',system-ui,sans-serif";
const mono = "'DM Mono',ui-monospace,monospace";

const toDate = (v) => (!v ? null : typeof v?.toDate === 'function' ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v instanceof Date ? v : new Date(v));
const fmtTime = (v) => { const d = toDate(v); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'; };
const fmtDT = (v) => { const d = toDate(v); return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; };
const dayLabel = (key) => { if (!key) return ''; const [y, m, d] = key.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }); };
const isRecentlyChanged = (b) => {
  const u = toDate(b.updatedAt)?.getTime() || 0; const r = toDate(b.reassignedAt)?.getTime() || 0; const c = toDate(b.createdAt)?.getTime() || 0;
  const e = Math.max(u, r); if (e && c && Math.abs(e - c) < 1000) return false; return e > 0 && Date.now() - e <= 18 * 3600 * 1000;
};
const toDateInput = (v) => { const d = toDate(v) || new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const toTimeInput = (v) => { const d = toDate(v); return d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '09:00'; };

const STATUS = {
  canceled: { bg: C.redDim, fg: C.red, label: 'Cancelled' },
  completed: { bg: C.greenDim, fg: C.green, label: 'Completed' },
  no_show: { bg: C.amberDim, fg: C.amber, label: 'No-show' },
  confirmed: { bg: C.blueDim, fg: C.blue, label: 'Confirmed' },
  in_progress: { bg: C.blueDim, fg: C.blue, label: 'In progress' },
};

export default function OpsSchedulePanel({ orgId, organization = null, members = [], currentUser, currentUserName }) {
  // Pass 82C.1 — generic schedule runtime derives "current season" + wall-clock timezone from the SELECTED org,
  // never a module-global. SAS org resolves 2026_27 / Asia/Tokyo → identical behavior.
  const currentSeasonId = resolveOrgSeasonId(organization);
  const orgTz = resolveOrgTimezone(organization);
  const sched = useOrgSchedule(orgId);
  const [view, setView] = useState('tomorrow'); // tomorrow | today | date | week
  const [pickedDate, setPickedDate] = useState(toDateInput(new Date(Date.now() + 86400000)));
  const [instructorFilter, setInstructorFilter] = useState('all'); // all | unassigned | <userId>
  const [showCancelled, setShowCancelled] = useState(true);
  const [section, setSection] = useState('schedule'); // schedule | sync | audit
  const [seasonFilter, setSeasonFilter] = useState(currentSeasonId);
  const [editing, setEditing] = useState(null); // booking object or {__new:true}
  const [toast, setToast] = useState('');
  const historical = isHistoricalSeason(seasonFilter, currentSeasonId);

  // Season-scoped bookings (Pass 46 multi-season). Historical seasons are read-only.
  const seasonBookings = useMemo(() => (sched.all || []).filter((b) => deriveSeasonId(b) === seasonFilter), [sched.all, seasonFilter]);
  // Pass 47C §4: switching to a PAST season must reset the stale current date (Aug 2026)
  // to that season's actual range — never leave a historical view showing today's date.
  useEffect(() => {
    if (historical) {
      const first = seasonBookings.map((b) => toDate(b.startAt)).filter(Boolean).sort((a, b) => a - b)[0];
      if (first) { setPickedDate(toDateInput(first)); setView('date'); }
    } else { setView('tomorrow'); }
  }, [seasonFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const jumpToNextDay = () => {
    const now = Date.now();
    const upcoming = seasonBookings.map((b) => toDate(b.startAt)).filter((d) => d && d.getTime() >= now).sort((a, b) => a - b)[0]
      || seasonBookings.map((b) => toDate(b.startAt)).filter(Boolean).sort((a, b) => a - b)[0];
    if (upcoming) { setPickedDate(toDateInput(upcoming)); setView('date'); flash(`Jumped to next scheduled day: ${toDateInput(upcoming)}`); }
    else flash('No scheduled days in this season.');
  };

  // Pass 47C §2/§16: the Season roster must derive from the SELECTED season — NOT all
  // current/demo members. getMembersForSeason returns that season's people (membership
  // + anyone booked that season), then we split out non-teaching staff (drivers).
  const instructors = useMemo(() => {
    const seasonRoster = getMembersForSeason(members, sched.all || [], seasonFilter);
    const { teaching } = splitTeachingStaff(seasonRoster);
    return teaching
      .map((m) => ({
        id: m.userId || m.id,
        name: m.name || m.email,
        skiLevel: m.skiLevel,
        snowboardLevel: m.snowboardLevel,
        primaryDiscipline: m.primaryDiscipline || '',
        category: resolveInstructorCategory(m, historical ? { season: seasonFilter } : {}),
      }))
      .filter((m) => m.id);
  }, [members, sched.all, historical, seasonFilter]);
  const nameOf = useMemo(() => Object.fromEntries(instructors.map((i) => [i.id, i.name])), [instructors]);

  const activeKey = view === 'today' ? sched.todayKey : view === 'tomorrow' ? sched.tomorrowKey : pickedDate;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const visible = useMemo(() => {
    let list;
    if (view === 'week') list = sched.week || [];
    else list = (sched.byDay?.[activeKey] || []);
    list = list.filter((b) => deriveSeasonId(b) === seasonFilter);
    if (instructorFilter === 'unassigned') list = list.filter((b) => !b.instructorId);
    else if (instructorFilter !== 'all') list = list.filter((b) => b.instructorId === instructorFilter);
    if (!showCancelled) list = list.filter((b) => b.status !== 'canceled');
    // Pass 59B — a COVERED grid hold is already shown by its real client booking; never render it twice.
    list = list.filter((b) => !isCoveredGridHold(b));
    return list;
  }, [sched, view, activeKey, instructorFilter, showCancelled, seasonFilter]);

  // group week view by day
  const grouped = useMemo(() => {
    if (view !== 'week') return [[activeKey, visible]];
    const g = {}; visible.forEach((b) => { const k = orgDateKey(b.startAt); (g[k] = g[k] || []).push(b); });
    return Object.keys(g).sort().map((k) => [k, g[k]]);
  }, [view, visible, activeKey]);

  return (
    <div style={{ fontFamily: font, color: C.t1 }}>
      {/* section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['schedule', 'Schedule'], ['sync', 'Sync status'], ['audit', 'Audit history']].map(([k, l]) => (
          <button key={k} onClick={() => setSection(k)} style={pill(section === k)}>{l}</button>
        ))}
        <div style={{ flex: 1 }} />
        {/* Season selector (Pass 46) */}
        <select value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)} style={{ ...inp(120), fontWeight: 700 }} title="Season">
          {SEASONS.map((s) => <option key={s} value={s}>{seasonLabel(s)}{s === currentSeasonId ? ' · current' : ''}</option>)}
        </select>
        {/* Pass 47C §1/§17/§23: environment ≠ realtime. Show env explicitly; "Realtime" not "Live". */}
        {/* Pass 82C — environment badge shows the SELECTED org identity dynamically, never a hard-coded 'sas-niseko'. */}
        {process.env.REACT_APP_USE_EMULATOR === 'true'
          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#B45309', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '3px 8px' }} title={`Local Firebase emulator — ${orgId}. NOT production.`}>● LOCAL · EMULATOR — DEMO DATA ({orgId})</span>
          : <span style={{ fontSize: 11, fontWeight: 700, color: '#065F46', background: '#D1FAE5', border: '1px solid #10B981', borderRadius: 6, padding: '3px 8px' }} title={`Production Firestore — ${orgId}.`}>PRODUCTION · {orgId}</span>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.t2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: sched.error ? C.red : C.green, boxShadow: `0 0 8px ${sched.error ? C.red : C.green}` }} />
          {sched.error ? 'Disconnected' : 'Realtime'}{sched.lastUpdatedAt ? ` · ${fmtTime(sched.lastUpdatedAt)}` : ''}
        </span>
      </div>

      {historical && (
        <div style={{ background: 'rgba(217,119,6,0.10)', border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 10, padding: '9px 14px', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
          📚 Viewing <b>{seasonLabel(seasonFilter)}</b> — a past season (read-only). Live scheduling actions are hidden; these records are historical.
        </div>
      )}

      {toast && <div style={{ background: C.greenDim, border: `1px solid ${C.green}`, color: C.green, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 600 }}>{toast}</div>}

      {section === 'schedule' && (
        <>
          {/* controls */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            {[['tomorrow', 'Tomorrow'], ['today', 'Today'], ['date', 'Pick date'], ['week', 'Week'], ['season', 'Season']].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} style={pill(view === k)}>{l}</button>
            ))}
            {view === 'date' && <input type="date" value={pickedDate} onChange={(e) => setPickedDate(e.target.value)} style={inp(150)} />}
            <select value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)} style={inp(180)}>
              <option value="all">All instructors</option>
              <option value="unassigned">Unassigned only</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <label style={{ fontSize: 13, color: C.t2, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} /> Show cancelled
            </label>
            <div style={{ flex: 1 }} />
            <button onClick={jumpToNextDay} style={pill(false)} title="Go to the first upcoming day with scheduled lessons">↪ Next scheduled day</button>
            {!historical && <button onClick={() => setEditing({ __new: true, startAt: wallClockToInstant(view === 'today' ? sched.todayKey : view === 'tomorrow' ? sched.tomorrowKey : pickedDate, '09:00', orgTz), endAt: wallClockToInstant(view === 'today' ? sched.todayKey : view === 'tomorrow' ? sched.tomorrowKey : pickedDate, '11:00', orgTz) })} style={{ ...pill(true), background: C.green, borderColor: C.green }}>+ New lesson</button>}
          </div>

          {view === 'season' && (
            <SeasonOverview bookings={seasonBookings} instructors={instructors} onSelectBooking={(b) => setEditing(b)} />
          )}

          {view !== 'season' && sched.loading && <div style={{ color: C.t3, fontSize: 14 }}>Loading schedule…</div>}
          {view !== 'season' && !sched.loading && grouped.map(([key, list]) => (
            <div key={key} style={{ marginBottom: 20 }}>
              {view === 'week' && <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, margin: '6px 0 10px' }}>{dayLabel(key)}</div>}
              {list.length === 0 && <div style={{ fontSize: 14, color: C.t3, border: `1px dashed ${C.border}`, borderRadius: 12, padding: 18, textAlign: 'center' }}>No lessons for this day/filter.</div>}
              {list.map((b) => (
                <OpsCard key={b.id} b={b} readOnly={historical} conflict={sched.conflicts?.has(b.id)} instructorName={b.instructorId ? (nameOf[b.instructorId] || b.instructorNameSnapshot || 'Instructor') : ''}
                  onEdit={() => setEditing(b)}
                  onCancel={async () => { await cancelBooking(db, { booking: b, handledByUserId: currentUser?.uid, actorUserId: currentUser?.uid, actorName: currentUserName }); flash('Lesson cancelled — instructor notified.'); }}
                  onRestore={async () => { await restoreBooking(db, { booking: b, actorUserId: currentUser?.uid, actorName: currentUserName }); flash('Lesson restored.'); }}
                  onUnassign={async () => { await unassignBooking(db, { bookingId: b.id, actorUserId: currentUser?.uid, actorName: currentUserName }); flash('Lesson unassigned.'); }}
                />
              ))}
            </div>
          ))}
        </>
      )}

      {section === 'sync' && <SyncStatusPanel orgId={orgId} />}
      {section === 'audit' && <AuditHistoryPanel orgId={orgId} nameOf={nameOf} />}

      {editing && (
        <LessonEditor
          booking={editing} instructors={instructors} orgId={orgId} organization={organization} currentUser={currentUser} currentUserName={currentUserName}
          onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); flash(msg); }}
        />
      )}
    </div>
  );
}

function OpsCard({ b, conflict, instructorName, onEdit, onCancel, onRestore, onUnassign, readOnly }) {
  const cancelled = b.status === 'canceled';
  const changed = isRecentlyChanged(b);
  const st = STATUS[b.status] || { bg: C.card2, fg: C.t2, label: b.status };
  const [busy, setBusy] = useState('');
  const run = (fn, key) => async () => { setBusy(key); try { await fn(); } catch (e) { alert(e.message); } finally { setBusy(''); } };
  return (
    <div style={{ background: C.card, border: `1px solid ${conflict ? C.red : changed ? 'rgba(217,119,6,0.4)' : C.border}`, borderLeft: `4px solid ${cancelled ? C.red : b.instructorId ? C.blue : C.amber}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, opacity: cancelled ? 0.7 : 1 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: mono, fontSize: 16, fontWeight: 700 }}>{b.hasOperationalTime === false || !b.startAt ? <em style={{ color: C.t3, fontSize: 13 }}>Time not recorded</em> : `${fmtTime(b.startAt)}–${fmtTime(b.endAt)}`}</span>
        <span style={{ fontWeight: 700 }}>{instructorName || <em style={{ color: C.amber }}>Unassigned</em>}</span>
        {/* Pass 59B — a schedule-grid hold shows its provisional booking label (clean), never a client field. */}
        <span style={{ color: C.t2, fontSize: 13 }}>· {isGridHold(b) ? `${holdSlotLabel(b)} · ${b.sourceScheduleLabel || 'Hold'}` : (b.serviceNameSnapshot || b.serviceType || 'Lesson')}</span>
        {b.discipline && <span style={{ fontSize: 11, background: C.card2, color: C.t2, padding: '1px 8px', borderRadius: 20 }}>{b.discipline}</span>}
        <div style={{ flex: 1 }} />
        {conflict && <Badge fg={C.red} bg={C.redDim}>⚠ CONFLICT</Badge>}
        {changed && !cancelled && <Badge fg={C.amber} bg={C.amberDim}>● CHANGED</Badge>}
        {/* Grid hold → a clear "HOLD" pill (purple); other non-client entries keep their type label. */}
        {isGridHold(b) && <Badge fg="#fff" bg="#7C3AED">{holdSlotLabel(b).toUpperCase()} · HOLD</Badge>}
        {!isClientBooking(b) && !isGridHold(b) && <Badge fg="#fff" bg={SCHEDULE_ENTRY_TYPES[b.scheduleEntryType]?.color || '#57564F'}>{(SCHEDULE_ENTRY_TYPES[b.scheduleEntryType]?.label || 'Event').toUpperCase()}</Badge>}
        {isClientBooking(b) && b.paymentState && PAYMENT_STATES[b.paymentState] && <Badge fg={PAYMENT_STATES[b.paymentState].color} bg={PAYMENT_STATES[b.paymentState].bg}>{PAYMENT_STATES[b.paymentState].short}</Badge>}
        {cancelled && b.cancellationResolution && CANCELLATION_RESOLUTIONS[b.cancellationResolution] && <Badge fg={CANCELLATION_RESOLUTIONS[b.cancellationResolution].color} bg={C.card2}>{CANCELLATION_RESOLUTIONS[b.cancellationResolution].label}</Badge>}
        {(() => { const r = assessOperationalReadiness(b); return !r.ready ? <Badge fg={C.amber} bg={C.amberDim} title={`Missing: ${r.missing.join(', ')}`}>⚠ NEEDS: {r.missing.join(', ')}</Badge> : null; })()}
        <Badge fg={st.fg} bg={st.bg}>{st.label}</Badge>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6, fontSize: 12.5, color: C.t2 }}>
        {isClientBooking(b) && <span>Guest: <b style={{ color: C.t1 }}>{b.requesterName || '—'}</b></span>}
        {(b.groupSize != null) && <span>Group: {b.groupSize}{(b.additionalGuests || []).length ? ` (+${b.additionalGuests.length})` : ''}</span>}
        {b.skillLevel && SKILL_LABELS[b.skillLevel] && <span>Level: {SKILL_LABELS[b.skillLevel]}</span>}
        <span>Meet: {b.locationLabel || '—'}</span>
        {b.accommodationMapUrl && <a href={b.accommodationMapUrl} target="_blank" rel="noreferrer" style={{ color: C.blue, textDecoration: 'underline' }}>📍 Map</a>}
        {b.language && <span>Lang: {b.language}</span>}
        {b.roomBossId && <span style={{ color: C.t3 }}>RB: {b.roomBossId}</span>}
        {b.invoiceNumber && <span style={{ color: C.t3 }}>Inv: {b.invoiceNumber}</span>}
        {b.isReferral && <span style={{ color: '#9333EA' }}>↩ Referral{b.referredByName ? ` (${b.referredByName})` : ''}</span>}
        <span style={{ color: C.t3 }}>Src: {b.source || b.externalSource || 'manual'}</span>
        <span style={{ color: C.t3 }}>Updated {fmtDT(b.updatedAt)}</span>
      </div>
      {readOnly && <div style={{ marginTop: 8, fontSize: 11, color: C.t3, fontStyle: 'italic' }}>Historical record — read-only</div>}
      {!readOnly && (
      <div style={{ display: 'flex', gap: 7, marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={onEdit} style={btn()}>Edit / Assign</button>
        {b.instructorId && !cancelled && <button disabled={!!busy} onClick={run(onUnassign, 'u')} style={btn()}>{busy === 'u' ? '…' : 'Unassign'}</button>}
        {!cancelled && <button disabled={!!busy} onClick={run(onCancel, 'c')} style={btn(C.red)}>{busy === 'c' ? '…' : 'Cancel'}</button>}
        {cancelled && <button disabled={!!busy} onClick={run(onRestore, 'r')} style={btn(C.green)}>{busy === 'r' ? '…' : 'Restore'}</button>}
      </div>
      )}
    </div>
  );
}

function LessonEditor({ booking, instructors, orgId, organization = null, currentUser, currentUserName, onClose, onSaved }) {
  const isNew = !!booking.__new;
  // Pass 82C.1 — display + construct times in the ORGANIZATION's timezone, never the browser's. So an operator in any
  // timezone edits the same wall-clock the booking was created in (no shifted times).
  const leTz = resolveOrgTimezone(organization);
  const [f, setF] = useState({
    date: booking.startAt ? zonedDateKey(booking.startAt, leTz) : toDateInput(booking.startAt),
    start: booking.startAt ? zonedTimeLabel(booking.startAt, leTz) : toTimeInput(booking.startAt),
    end: booking.endAt ? zonedTimeLabel(booking.endAt, leTz) : toTimeInput(booking.endAt),
    instructorId: booking.instructorId || '', requesterName: booking.requesterName || '',
    serviceName: booking.serviceNameSnapshot || booking.serviceType || '', discipline: booking.discipline || 'ski',
    locationLabel: booking.locationLabel || '', groupSize: booking.groupSize != null ? String(booking.groupSize) : '',
    language: booking.language || '', transportInfo: booking.transportInfo || '', notes: booking.notes || '', internalNotes: '',
    // Pass 43 workbook fields
    scheduleEntryType: booking.scheduleEntryType || 'client_booking',
    bookingType: booking.bookingType || '', skillLevel: booking.skillLevel || '',
    paymentState: booking.paymentState || '', cancellationResolution: booking.cancellationResolution || '',
    leadDob: booking.leadGuest?.dob || '',
    additionalGuestsText: formatAdditionalGuests(booking.additionalGuests || []),
    accommodationName: booking.accommodationName || '', accommodationAddress: booking.accommodationAddress || '',
    accommodationMapUrl: booking.accommodationMapUrl || '', roomBossId: booking.roomBossId || '',
    invoiceNumber: booking.invoiceNumber || '', referredByName: booking.referredByName || '',
  });
  const selectedInstructor = instructors.find((i) => i.id === f.instructorId);
  const compat = f.instructorId && f.scheduleEntryType === 'client_booking'
    ? checkDisciplineCompatibility(selectedInstructor || {}, f.discipline) : { compatible: true, warning: '' };
  const isInternal = f.scheduleEntryType !== 'client_booking';
  const cancelled = booking.status === 'canceled';
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!isNew && booking.id) getBookingInternal(db, booking.id).then((d) => { if (d?.internalNotes) setF((p) => ({ ...p, internalNotes: d.internalNotes })); }).catch(() => {});
  }, [isNew, booking.id]);

  const save = async () => {
    setErr('');
    const startAt = wallClockToInstant(f.date, f.start, leTz); const endAt = wallClockToInstant(f.date, f.end, leTz);
    if (endAt.getTime() <= startAt.getTime()) { setErr('Finish time must be after start time.'); return; }
    if (!orgId) { setErr('Organisation missing.'); return; }
    if (f.instructorId && !instructors.some((i) => i.id === f.instructorId)) { setErr('Instructor is not in this organisation.'); return; }
    setSaving(true);
    try {
      const groupSize = f.groupSize === '' ? null : Number(f.groupSize);
      const instructorName = instructors.find((i) => i.id === f.instructorId)?.name || '';
      const extraFields = {
        scheduleEntryType: f.scheduleEntryType,
        bookingType: f.bookingType,
        durationCategory: deriveDurationCategory(f.bookingType),
        skillLevel: f.skillLevel,
        discipline: f.discipline,
        paymentState: isInternal ? '' : f.paymentState,
        cancellationResolution: cancelled ? f.cancellationResolution : '',
        leadGuest: { fullName: f.requesterName, dob: f.leadDob, email: booking.leadGuest?.email || '', phone: booking.leadGuest?.phone || '' },
        additionalGuests: parseAdditionalGuests(f.additionalGuestsText),
        accommodationName: f.accommodationName,
        accommodationAddress: f.accommodationAddress,
        accommodationMapUrl: f.accommodationMapUrl,
        roomBossId: f.roomBossId,
        invoiceNumber: f.invoiceNumber,
        referredByName: f.referredByName,
        isReferral: Boolean(f.referredByName),
      };
      if (isNew) {
        await createManualBooking(db, {
          organizationId: orgId, organization, timezone: leTz, instructorId: f.instructorId, instructorName, requesterName: f.requesterName,
          serviceName: f.serviceName, discipline: f.discipline, startAt, endAt, locationLabel: f.locationLabel,
          groupSize, language: f.language, transportInfo: f.transportInfo, notes: f.notes, internalNotes: f.internalNotes,
          actorUserId: currentUser?.uid, actorName: currentUserName, extraFields,
        });
        onSaved('Lesson created.');
      } else {
        // Field edits (time/location/notes/internal/etc.) → updateBooking
        await updateBooking(db, booking.id, {
          startAt, endAt, locationLabel: f.locationLabel, notes: f.notes, internalNotes: f.internalNotes,
          groupSize, language: f.language, transportInfo: f.transportInfo,
          actorUserId: currentUser?.uid, actorName: currentUserName, extraFields,
        });
        // Instructor change → assign / reassign / unassign
        if ((f.instructorId || '') !== (booking.instructorId || '')) {
          if (!f.instructorId) await unassignBooking(db, { bookingId: booking.id, actorUserId: currentUser?.uid, actorName: currentUserName });
          else await assignInstructorToBooking(db, { bookingId: booking.id, instructorId: f.instructorId, instructorName, assignedByUserId: currentUser?.uid, assignedByName: currentUserName });
        }
        onSaved('Lesson updated — instructor notified.');
      }
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 24, width: 560, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800 }}>{isNew ? 'New lesson' : 'Edit lesson'}</h3>
        {err && <div style={{ background: C.redDim, color: C.red, border: `1px solid ${C.red}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Fld label="Date"><input type="date" value={f.date} onChange={set('date')} style={inp()} /></Fld>
          <Fld label="Start"><input type="time" value={f.start} onChange={set('start')} style={inp()} /></Fld>
          <Fld label="Finish"><input type="time" value={f.end} onChange={set('end')} style={inp()} /></Fld>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
          <Fld label="Entry type">
            <select value={f.scheduleEntryType} onChange={set('scheduleEntryType')} style={inp()}>
              {Object.entries(SCHEDULE_ENTRY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}{v.isClient ? '' : ' (internal)'}</option>)}
            </select>
          </Fld>
          {!isInternal && (
            <Fld label="Payment status">
              <select value={f.paymentState} onChange={set('paymentState')} style={inp()}>
                <option value="">— not set —</option>
                {Object.entries(PAYMENT_STATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Fld>
          )}
          {cancelled && (
            <Fld label="Cancellation resolution">
              <select value={f.cancellationResolution} onChange={set('cancellationResolution')} style={inp()}>
                <option value="">— not set —</option>
                {Object.entries(CANCELLATION_RESOLUTIONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Fld>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <Fld label="Instructor">
            <select value={f.instructorId} onChange={set('instructorId')} style={inp()}>
              <option value="">— Unassigned —</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </Fld>
          <Fld label="Guest / group"><input value={f.requesterName} onChange={set('requesterName')} style={inp()} /></Fld>
          <Fld label="Service"><input value={f.serviceName} onChange={set('serviceName')} style={inp()} /></Fld>
          <Fld label="Discipline">
            <select value={f.discipline} onChange={set('discipline')} style={inp()}>
              {Object.entries(DISCIPLINES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Fld>
          <Fld label="Meeting location"><input value={f.locationLabel} onChange={set('locationLabel')} style={inp()} /></Fld>
          <Fld label="Group size"><input type="number" min="0" value={f.groupSize} onChange={set('groupSize')} style={inp()} /></Fld>
          <Fld label="Language"><input value={f.language} onChange={set('language')} style={inp()} /></Fld>
          <Fld label="Transport / pickup"><input value={f.transportInfo} onChange={set('transportInfo')} style={inp()} /></Fld>
        </div>

        {/* Discipline compatibility warning (item 6) — warn, never block */}
        {!compat.compatible && (
          <div style={{ background: C.amberDim, border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 8, padding: '8px 12px', marginTop: 8, fontSize: 12.5, fontWeight: 600 }}>
            ⚠ {compat.warning} You can still assign — this is a warning only.
          </div>
        )}

        {!isInternal && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
            <Fld label="Booking type (Sam's)"><input value={f.bookingType} onChange={set('bookingType')} placeholder="e.g. MOS - Full Day" style={inp()} /></Fld>
            <Fld label="Skill level">
              <select value={f.skillLevel} onChange={set('skillLevel')} style={inp()}>
                <option value="">— not set —</option>
                {Object.entries(SKILL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Fld>
            <Fld label="Lead guest DOB"><input value={f.leadDob} onChange={set('leadDob')} placeholder="DD/MM/YYYY" style={inp()} /></Fld>
            <Fld label="Additional guests (Name, DOB. …)"><input value={f.additionalGuestsText} onChange={set('additionalGuestsText')} placeholder="Harold, 42. Kumar, 20." style={inp()} /></Fld>
            <Fld label="Accommodation name"><input value={f.accommodationName} onChange={set('accommodationName')} style={inp()} /></Fld>
            <Fld label="Accommodation address"><input value={f.accommodationAddress} onChange={set('accommodationAddress')} style={inp()} /></Fld>
            <Fld label="Accommodation map URL"><input value={f.accommodationMapUrl} onChange={set('accommodationMapUrl')} placeholder="https://maps.app.goo.gl/…" style={inp()} /></Fld>
            <Fld label="Referred by (instructor/person)"><input value={f.referredByName} onChange={set('referredByName')} style={inp()} /></Fld>
            <Fld label="RoomBoss number"><input value={f.roomBossId} onChange={set('roomBossId')} style={inp()} /></Fld>
            <Fld label="Invoice number"><input value={f.invoiceNumber} onChange={set('invoiceNumber')} style={inp()} /></Fld>
          </div>
        )}

        <Fld label={isInternal ? 'Event notes' : 'Guest-facing notes'}><textarea value={f.notes} onChange={set('notes')} style={{ ...inp(), minHeight: 46, resize: 'vertical' }} /></Fld>
        <Fld label="Staff-only ops notes (never shown to clients)"><textarea value={f.internalNotes} onChange={set('internalNotes')} style={{ ...inp(), minHeight: 46, resize: 'vertical', background: C.amberDim }} /></Fld>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={btn()}>Cancel</button>
          <button disabled={saving} onClick={save} style={{ ...pill(true), background: C.green, borderColor: C.green, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : (isNew ? 'Create lesson' : 'Save changes')}</button>
        </div>
      </div>
    </div>
  );
}

export function SyncStatusPanel({ orgId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const q = query(collection(db, 'scheduleImports'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setRows([]));
    return () => unsub();
  }, [orgId]);
  if (rows === null) return <div style={{ color: C.t3 }}>Loading sync status…</div>;
  if (!rows.length) return <div style={{ color: C.t3, fontSize: 14 }}>No imports yet. RoomBoss remains intentionally unconfigured; use the JSON/CSV/seed adapters.</div>;
  return (
    <div>
      {rows.map((r) => (
        <div key={r.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <b>{r.source}</b>
            <Badge fg={r.status === 'success' ? C.green : r.status === 'failed' ? C.red : C.amber} bg={r.status === 'success' ? C.greenDim : r.status === 'failed' ? C.redDim : C.amberDim}>{r.status}</Badge>
            <span style={{ color: C.t3, fontSize: 12 }}>Last sync {fmtDT(r.finishedAt || r.createdAt)}</span>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: C.t2, flexWrap: 'wrap' }}>
            <span>Created <b>{r.created ?? 0}</b></span><span>Updated <b>{r.updated ?? 0}</b></span>
            <span>Unchanged <b>{r.unchanged ?? 0}</b></span><span>Removed/cancelled <b>{r.cancelledMissing ?? 0}</b></span>
            <span>Failed <b style={{ color: r.failed ? C.red : C.t2 }}>{r.failed ?? 0}</b></span>
            <span>Needs review <b>{(r.needsReview || []).length}</b></span>
          </div>
          {(r.needsReview || []).length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.amber, background: C.amberDim, borderRadius: 8, padding: '8px 10px' }}>
              {r.needsReview.map((n, i) => <div key={i}>• {n.externalId ? `${n.externalId}: ` : ''}{n.reason}</div>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function AuditHistoryPanel({ orgId, nameOf }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const q = query(collection(db, 'scheduleChanges'), where('organizationId', '==', orgId), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => { console.warn(e); setRows([]); });
    return () => unsub();
  }, [orgId]);
  if (rows === null) return <div style={{ color: C.t3 }}>Loading audit history…</div>;
  if (!rows.length) return <div style={{ color: C.t3, fontSize: 14 }}>No schedule changes recorded yet.</div>;
  return (
    <div>
      {rows.map((r) => (
        <div key={r.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge fg={C.blue} bg={C.blueDim}>{r.changeType}</Badge>
            <span style={{ color: C.t3, fontSize: 12, fontFamily: mono }}>{fmtDT(r.createdAt)}</span>
            <span style={{ color: C.t2, fontSize: 12 }}>by {r.changedByName || r.changedByUserId || 'system'}</span>
            {r.notificationSent && <span style={{ color: C.green, fontSize: 11 }}>✓ notified</span>}
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: C.t2 }}>
            {(r.changes || []).map((c, i) => (
              <div key={i}>{c.label}: <span style={{ color: C.red }}>{c.previousValue || '∅'}</span> → <span style={{ color: C.green }}>{c.newValue || '∅'}</span></div>
            ))}
            {r.previousInstructorId && <div>Instructor: {nameOf[r.previousInstructorId] || r.previousInstructorId} → {nameOf[r.newInstructorId] || r.newInstructorId || '∅'}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── tiny style helpers ──
function pill(active) { return { padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: font, border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : C.card, color: active ? '#fff' : C.t2 }; }
function btn(color) { return { padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: font, border: `1px solid ${color || C.border}`, background: C.card, color: color || C.t2 }; }
function inp(w) { return { width: w ? undefined : '100%', minWidth: w || undefined, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: font, color: C.t1 }; }
function Badge({ children, fg, bg }) { return <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3, color: fg, background: bg, padding: '2px 9px', borderRadius: 20 }}>{children}</span>; }
function Fld({ label, children }) { return <label style={{ display: 'block', marginTop: 10 }}><span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.t3, display: 'block', marginBottom: 4 }}>{label}</span>{children}</label>; }
